import { useEffect } from "react";
import { MapPin, Phone, Mail, MessageCircle, Clock } from "lucide-react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import LeadForm from "@/components/common/LeadForm";
import { cityConfig } from "@/config/cityConfig";
import { useMeta } from "@/hooks/useMeta";

export default function KontaktPage() {
  useMeta(
    "Kontakt | BRIMO Immobilien NRW",
    "Kontaktieren Sie BRIMO Immobilien NRW. Wilhelmstraße 3, 42781 Haan. Tel: +49 2129 3766829. Persönlicher Ansprechpartner."
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      <Navbar />

      {/* Header */}
      <section className="pt-32 pb-16 bg-[#0d0d0d]" data-testid="kontakt-header">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="h-0.5 w-12 bg-[#E61D38] mb-5" />
          <h1
            className="text-4xl sm:text-5xl font-black text-white uppercase tracking-tight"
            style={{ fontFamily: "Montserrat, sans-serif" }}
          >
            KONTAKT
          </h1>
          <p className="text-gray-400 mt-4 max-w-xl text-sm leading-relaxed">
            Wir sind für Sie da – persönlich, diskret und zuverlässig. Kontaktieren Sie uns
            telefonisch, per WhatsApp oder über das Formular.
          </p>
        </div>
      </section>

      {/* Contact Info + Form */}
      <section className="py-16 md:py-24 bg-[#1a1a1a]" data-testid="kontakt-main">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <div className="h-0.5 w-12 bg-[#E61D38] mb-6" />
              <h2
                className="text-2xl md:text-3xl font-black text-white uppercase tracking-tight mb-8"
                style={{ fontFamily: "Montserrat, sans-serif" }}
              >
                DIREKT KONTAKTIEREN
              </h2>

              <div className="space-y-6 mb-10">
                <div
                  className="flex items-start gap-5 bg-[#262626] border border-white/5 p-6 hover:border-[#E61D38]/30 transition-colors"
                  data-testid="contact-address"
                >
                  <div className="bg-[#E61D38] p-3 flex-shrink-0">
                    <MapPin size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm uppercase tracking-wider mb-1">
                      Büro Haan
                    </p>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {cityConfig.address.street}
                      <br />
                      {cityConfig.address.postalCode} {cityConfig.address.city}
                      <br />
                      {cityConfig.address.country}
                    </p>
                  </div>
                </div>

                <a
                  href={`tel:${cityConfig.phone}`}
                  className="flex items-start gap-5 bg-[#262626] border border-white/5 p-6 hover:border-[#E61D38]/50 transition-colors group"
                  data-testid="contact-phone"
                >
                  <div className="bg-[#E61D38] p-3 flex-shrink-0">
                    <Phone size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm uppercase tracking-wider mb-1">
                      Telefon & Mobil
                    </p>
                    <p className="text-gray-300 group-hover:text-[#E61D38] transition-colors text-sm">
                      {cityConfig.phoneDisplay}
                    </p>
                    <p className="text-gray-600 text-xs mt-1">Persönlicher Ansprechpartner</p>
                  </div>
                </a>

                <a
                  href={`https://wa.me/${cityConfig.whatsapp}?text=Hallo%2C%20ich%20m%C3%B6chte%20eine%20Immobilienberatung%20vereinbaren.`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-5 bg-[#262626] border border-white/5 p-6 hover:border-[#E61D38]/50 transition-colors group"
                  data-testid="contact-whatsapp"
                >
                  <div className="bg-[#E61D38] p-3 flex-shrink-0">
                    <MessageCircle size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm uppercase tracking-wider mb-1">
                      WhatsApp
                    </p>
                    <p className="text-gray-300 group-hover:text-[#E61D38] transition-colors text-sm">
                      {cityConfig.phoneDisplay}
                    </p>
                    <p className="text-gray-600 text-xs mt-1">Nachricht senden</p>
                  </div>
                </a>

                <div
                  className="flex items-start gap-5 bg-[#262626] border border-white/5 p-6"
                  data-testid="contact-hours"
                >
                  <div className="bg-[#E61D38] p-3 flex-shrink-0">
                    <Clock size={18} className="text-white" />
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm uppercase tracking-wider mb-2">
                      Erreichbarkeit
                    </p>
                    <div className="text-gray-400 text-sm space-y-1">
                      <p>Mo–Fr: 09:00 – 18:00 Uhr</p>
                      <p>Sa: 10:00 – 14:00 Uhr</p>
                      <p className="text-gray-600 text-xs mt-2">
                        Außerhalb der Öffnungszeiten: WhatsApp oder Nachrichten hinterlassen
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Manager */}
              <div className="bg-[#0d0d0d] border border-white/5 p-6 flex items-center gap-5">
                <img
                  src={cityConfig.manager.photo}
                  alt={cityConfig.manager.name}
                  className="w-16 h-16 object-cover flex-shrink-0"
                  loading="lazy"
                />
                <div>
                  <p className="text-white font-bold text-sm">{cityConfig.manager.name}</p>
                  <p className="text-[#E61D38] text-xs uppercase tracking-wider mt-0.5">
                    {cityConfig.manager.title}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">Persönliche Ansprechpartnerin</p>
                </div>
              </div>

              {/* Google Maps */}
              <div className="mt-8" data-testid="contact-map">
                <h3
                  className="text-white font-bold text-sm uppercase tracking-wider mb-4"
                  style={{ fontFamily: "Montserrat, sans-serif" }}
                >
                  Standort
                </h3>
                <div className="relative overflow-hidden border border-white/5" style={{ paddingBottom: "56.25%" }}>
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2496.8!2d7.0119!3d51.1965!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8d653a8d4d0ed%3A0x4e4e4e4e4e4e4e4e!2sWilhelmstra%C3%9Fe%203%2C%2042781%20Haan!5e0!3m2!1sde!2sde!4v1"
                    className="absolute inset-0 w-full h-full"
                    style={{ border: 0, filter: "grayscale(80%) invert(92%) contrast(85%)" }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="BRIMO Immobilien NRW Standort Haan"
                  />
                </div>
              </div>
            </div>

            {/* Form */}
            <div>
              <LeadForm
                formType="contact"
                title="Nachricht senden"
                ctaText="Nachricht senden"
                showDistrict={false}
                showBudget={false}
                showPropertyType={false}
                pageSource="/kontakt"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
